import React, { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import "./Dialog.scss";

/** How long the closing animation runs, in ms. */
export const DIALOG_EXIT_MS = 180;

export type DialogProps = {
  open: boolean;
  /** Asked to close: the ✕, Escape or a press on the backdrop. The caller sets `open` to false. */
  onClose: () => void;
  title?: React.ReactNode;
  /** A line under the title */
  subtitle?: React.ReactNode;
  /** An icon before the title */
  icon?: React.ReactNode;
  /** Drawer width: sm 420px · md 520px (default) · lg 720px · xl 1040px · full (the screen less the sidebar) */
  size?: "sm" | "md" | "lg" | "xl" | "full";
  /** Pinned under the body: the dialog's buttons, right-aligned */
  footer?: React.ReactNode;
  /** A press on the backdrop closes it (default true). Turn off for long forms. */
  closeOnBackdrop?: boolean;
  /** Busy: Escape, the backdrop and the ✕ do nothing (e.g. while saving) */
  busy?: boolean;
  /** Hide the ✕ */
  hideClose?: boolean;
  /**
   * Every dialog in the dashboard is a drawer: a full-height panel sliding in from the right (the owner's rule —
   * "dialogs should all be drawers, keep it consistent"). "center" is kept only for a future exception.
   */
  placement?: "center" | "side";
  className?: string;
  bodyClassName?: string;
  children?: React.ReactNode;
};

let openCount = 0;

/** Floating layers that sit above a dialog and take Escape themselves. */
const FLOATING_LAYERS = ".ui-datepicker-popper, .react-datepicker-popper, .clash-select__list, .ui-menu, .ui-color__pop";

/**
 * The one dialog shell: a backdrop, a white panel with a header (title, subtitle, ✕), a scrolling body and a footer.
 * It renders into document.body, closes on Escape, keeps the page behind from scrolling, moves the focus in on open
 * and back on close, and plays a short exit animation after `open` turns false.
 */
export function Dialog({
  open,
  onClose,
  title,
  subtitle,
  icon,
  size = "md",
  footer,
  closeOnBackdrop = true,
  busy = false,
  hideClose = false,
  placement = "side",
  className,
  bodyClassName,
  children,
}: DialogProps): React.JSX.Element | null {
  const [mounted, setMounted] = useState(open);
  const [closing, setClosing] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const returnFocus = useRef<HTMLElement | null>(null);
  const titleId = useId();
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const busyRef = useRef(busy);
  busyRef.current = busy;
  // The element to give focus back to: whatever had it when the dialog opened (read before the panel mounts)
  const wasOpen = useRef(false);
  if (open && !wasOpen.current && typeof document !== "undefined") {
    returnFocus.current = document.activeElement as HTMLElement | null;
  }
  wasOpen.current = open;

  useEffect(() => {
    if (open) {
      setMounted(true);
      setClosing(false);
      return;
    }
    if (!mounted) return;
    setClosing(true);
    const t = window.setTimeout(() => {
      setMounted(false);
      setClosing(false);
    }, DIALOG_EXIT_MS);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Focus in on open, back to where it was on close; lock the page scroll while any dialog is up.
  useEffect(() => {
    if (!mounted) return;
    const panel = panelRef.current;
    // A child that took focus on mount (autoFocus, e.g. a confirm's OK button) keeps it, so Enter confirms
    if (!panel || !panel.contains(document.activeElement)) {
      const first = panel?.querySelector<HTMLElement>(
        "input:not([type=hidden]):not(:disabled), textarea:not(:disabled), select:not(:disabled)"
      );
      (first ?? panel)?.focus({ preventScroll: true });
    }
    openCount += 1;
    document.body.style.overflow = "hidden";
    return () => {
      openCount -= 1;
      if (openCount <= 0) {
        openCount = 0;
        document.body.style.overflow = "";
      }
      returnFocus.current?.focus?.({ preventScroll: true });
    };
  }, [mounted]);

  useEffect(() => {
    if (!mounted || closing) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented || busyRef.current) return;
      // An open picker, dropdown or menu closes first (it handles its own Escape)
      if (document.querySelector(FLOATING_LAYERS)) return;
      // Only the top-most dialog closes
      const panels = document.querySelectorAll(".ui-dialog");
      if (panels[panels.length - 1] !== panelRef.current) return;
      e.preventDefault();
      onCloseRef.current();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [mounted, closing]);

  if (!mounted) return null;

  const requestClose = () => {
    if (!busy) onClose();
  };

  return createPortal(
    <div
      className={`ui-dialog-backdrop ui-dialog-backdrop--${placement}${closing ? " is-closing" : ""}`}
      onMouseDown={(e) => {
        if (closeOnBackdrop && e.target === e.currentTarget) requestClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
        className={["ui-dialog", `ui-dialog--${size}`, `ui-dialog--${placement}`, closing ? "is-closing" : "", className]
          .filter(Boolean)
          .join(" ")}
      >
        {(title || !hideClose) && (
          <header className="ui-dialog__head">
            {icon ? <span className="ui-dialog__icon">{icon}</span> : null}
            <div className="ui-dialog__titles">
              {title ? (
                <h2 id={titleId} className="ui-dialog__title">
                  {title}
                </h2>
              ) : null}
              {subtitle ? <p className="ui-dialog__subtitle">{subtitle}</p> : null}
            </div>
            {!hideClose && (
              <button type="button" className="ui-dialog__close" aria-label="Close" onClick={requestClose} disabled={busy}>
                <X size={18} />
              </button>
            )}
          </header>
        )}
        <div className={["ui-dialog__body", bodyClassName].filter(Boolean).join(" ")}>{children}</div>
        {footer ? <footer className="ui-dialog__foot">{footer}</footer> : null}
      </div>
    </div>,
    document.body
  );
}

export default Dialog;
